import React, { Suspense, useEffect, useState } from 'react'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PostHogProvider } from 'posthog-js/react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'

import i18n from '@/core/i18n'
import { TranslationProvider } from '@/core/i18n/TranslationProvider'
import { router } from '@/core/router'
import { initSentry } from '@/core/sentry/config'
import { SentryIdentify } from '@/core/sentry/SentryIdentify'
import { store } from '@/core/store'
import { restoreAuthState } from '@/core/store/slices/auth'
import { NextThemeProvider } from '@/core/theme/NextThemeProvider'
import { getThemeStorageKey } from '@/core/theme/storage'
import { initAmplitude } from '@/shared/analytics/amplitude'
import { AmplitudeIdentify } from '@/shared/analytics/AmplitudeIdentify'
import { PostHogIdentify } from '@/shared/analytics/PostHogIdentify'
import { NotificationProvider } from '@/shared/context/NotificationProvider'
import PartnerCustomizationProvider from '@/shared/context/PartnerCustomizationProvider'
import '@/core/i18n'
import './index.css'

import { AuthInitializer } from './AuthInitializer'

// Initialize Sentry FIRST before any other code runs
initSentry()

// Initialize Amplitude early, in parallel with PostHog
initAmplitude()

const posthogOptions = {
	api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
	autocapture: true,
	defaults: '2025-05-24',
	capture_exceptions: true, // This enables capturing exceptions using Error Tracking
	debug: false,
} as const

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
})

store.dispatch(restoreAuthState())

const getInitialTheme = () => {
	const state = store.getState()
	return state.ui.theme || 'light'
}

const getInitialLanguage = () => {
	const state = store.getState()
	return state.ui.language || 'en-US'
}

i18n.changeLanguage(getInitialLanguage())

const AppRoot = () => (
	<React.StrictMode>
		<PostHogProvider
			apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
			options={posthogOptions}
		>
			<Provider store={store}>
				<QueryClientProvider client={queryClient}>
					<NextThemeProvider
						attribute="class"
						defaultTheme={getInitialTheme()}
						enableSystem={false}
						storageKey={getThemeStorageKey()}
					>
						<TranslationProvider>
							{/* Suspense fallback for loading translations */}
							<Suspense fallback={<div></div>}>
								<PartnerCustomizationProvider>
									<NotificationProvider>
										<AuthInitializer />
										<PostHogIdentify />
										<AmplitudeIdentify />
										<SentryIdentify />
										<RouterProvider router={router} />
									</NotificationProvider>
								</PartnerCustomizationProvider>
							</Suspense>
						</TranslationProvider>
					</NextThemeProvider>
				</QueryClientProvider>
			</Provider>
		</PostHogProvider>
	</React.StrictMode>
)

const RootWithSoftReset = () => {
	const [resetKey, setResetKey] = useState(0)

	useEffect(() => {
		const handleSoftReset = () => {
			setResetKey((current) => current + 1)
		}

		window.addEventListener('app:soft-reset', handleSoftReset)

		return () => {
			window.removeEventListener('app:soft-reset', handleSoftReset)
		}
	}, [])

	return <AppRoot key={resetKey} />
}

ReactDOM.createRoot(document.getElementById('root')!).render(
	<RootWithSoftReset />
)
